import React from 'react';
import { Space } from 'antd';
import { I18n } from '@aws-amplify/core';
import type { ModelDetails, ActionPlanOrigin } from '@/components/Settings/DataExtraction/ExtractionType/Model/types';
import * as S from '@/components/Settings/DataExtraction/Modals/styles';
import { LabelMediumRegular } from '@/components/Settings/DataExtraction/styles';

interface DetailsProps {
	modelDetails: ModelDetails;
}

function getReportTags(actionPlanOrigins: ActionPlanOrigin[], reportName?: string, hasActionPlan?: boolean): string[] {
	const resultsLabel = I18n.get('Results');
	const actionPlansLabel = I18n.get('Action plans');

	const originTags = actionPlanOrigins.map((origin) => `${actionPlansLabel} ${origin.custom_report_name}`);

	if (!reportName) return originTags;

	const resultTags = [
		`${resultsLabel} ${reportName}`,
		hasActionPlan ? `${actionPlansLabel} ${reportName}` : null
	].filter(Boolean) as string[];

	return [...originTags, ...resultTags];
}

export function Details({ modelDetails }: Readonly<DetailsProps>): JSX.Element {
	const selectedActionPlanColumns = modelDetails?.action_plan_columns?.columns || [];
	const actionPlanOrigins = modelDetails?.action_plan_origins || [];
	const reportName = modelDetails?.custom_report?.name;
	const hasActionPlan = modelDetails?.has_action_plan_extraction;

	const reportTags = getReportTags(actionPlanOrigins, reportName, hasActionPlan);

	return (
		<Space direction="vertical" size={32} style={{ width: '100%' }}>
			<Space direction="vertical" size={16}>
				<S.CustomSubtitle>{I18n.get('Content:')}</S.CustomSubtitle>
				<Space wrap>
					{reportTags.map((reportName, index) => (
						<S.ContentTag key={`report-${index}`}>
							<LabelMediumRegular>{reportName}</LabelMediumRegular>
						</S.ContentTag>
					))}
					{modelDetails?.data_extraction_tools?.map((tool, index) => (
						<S.ContentTag key={`tool-${index}`}>
							<LabelMediumRegular>{I18n.get(tool.title)}</LabelMediumRegular>
						</S.ContentTag>
					))}
				</Space>
			</Space>

			{selectedActionPlanColumns.length > 0 && (
				<Space direction="vertical" size={16}>
					<S.CustomSubtitle>{I18n.get('Content of action plans:')}</S.CustomSubtitle>
					<Space wrap>
						{selectedActionPlanColumns.map((column: string) => (
							<S.ContentTag key={column}>
								<LabelMediumRegular>{I18n.get(column)}</LabelMediumRegular>
							</S.ContentTag>
						))}
					</Space>
				</Space>
			)}
		</Space>
	);
}
